import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRentalsQuery } from '../../../../hooks/queries/useRentalsQuery';
import { DataItem, updateDailyRental } from '../../../../services/apiSLTP/activities';
import { createDailyRental, DailyRental } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import {
    activityStateEnum,
    getMaterialStateInfos,
    getPriceTypeInfos,
    materialState,
    priceTypeEnum,
    stepModes,
    systemConfigKeyEnum,
} from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import DropDown, { OptionType } from '../../../DropDown';
import ModalBody, { ModalProps } from '../../ModalBody';
import ToggleLine from '../../../ToggleLine';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface ManageDailyRentalModalProps extends ModalProps {
    activity: DataItem;
    refetch: () => void;
    dailyRental: DailyRental | null;
    createTransfer: boolean;
}

const ManageDailyRentalModal: React.FC<ManageDailyRentalModalProps> = ({ open, handleClose, activity, refetch, dailyRental, createTransfer }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentRentalId, setCurrentRentalId] = useState(dailyRental?._id);
    const [selectedRentalType, setSelectedRentalType] = useState({ label: '', value: '' });
    const [rentalDesignation, setRentalDesignation] = useState('');
    const [observations, setObservations] = useState('');
    const [isCreateTransfer, setIsCreateTransfer] = useState(createTransfer);
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({ label: capitalizeFirstLetter(t('present')), value: materialState.PRESENT.toString() });
    const [price, setPrice] = useState(typeof dailyRental?.price === 'number' ? dailyRental?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyRental?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeInfos(dailyRental?.priceType))),
                  value: (dailyRental?.priceType).toString(),
              }
            : null
    );

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.NEW))), value: materialState.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.PRESENT))), value: materialState.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.REMOVE))), value: materialState.REMOVE.toString() },
    ];

    const {
        data: dataRentals,
        isLoading: isLoadingRentals,
        isError: isErrorRentals,
        isSuccess: isSuccessRentals,
        refetch: refetchRentals,
    } = useRentalsQuery({ queries: { active: true } });

    const postDailyRental = async () => {
        const response = await createDailyRental({
            activityId: activity._id,
            rentalTypeId: selectedRentalType.value,
            designation: rentalDesignation.trim(),
            state: parseInt(selectedState.value),
            createTransfer: isCreateTransfer,
            price: price ? parseFloat(price) : undefined,
            priceType: priceType?.value ? parseInt(priceType.value) : undefined,
            observations,
        });
        setCurrentRentalId(response.data._id);
        refetch();
    };

    const patchDailyRental = async () => {
        if (!currentRentalId) return;
        const response = await updateDailyRental(
            currentRentalId,
            {
                rentalTypeId: selectedRentalType.value,
                state: parseInt(selectedState.value),
                designation: rentalDesignation,
                createTransfer: isCreateTransfer,
                price: price ? parseFloat(price) : null,
                priceType: priceType?.value ? parseInt(priceType.value) : null,
                observations,
            },
            {
                syncTransfers: true,
            }
        );
        refetch();
    };

    useEffect(() => {
        if (dailyRental) {
            setSelectedRentalType({ label: dailyRental.rentalTypeId?.designation || '', value: dailyRental.rentalTypeId?._id || '' });
            setRentalDesignation(dailyRental.designation);
            setSelectedState({
                label: capitalizeFirstLetter(t(getMaterialStateInfos(dailyRental.state))) || '',
                value: dailyRental.state.toString() || '',
            });
            setObservations(dailyRental.observations || '');
        }
    }, []);

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('rental')}
            description={activity.siteId.name}
            subTitle={!!currentRentalId?.length ? t('update') : t('add')}
            okText={t('valider')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentRentalId?.length ? postDailyRental() : patchDailyRental();
            }}
            disabledOk={!(selectedRentalType.value.length && rentalDesignation.length && selectedState.value.length)}
        >
            <DropDown
                selectedItem={selectedRentalType}
                items={dataRentals?.map((value) => ({ label: value.designation, value: value._id })) || []}
                handleChange={(event, value) => setSelectedRentalType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
                loading={isLoadingRentals}
            />
            <CustomTextInput value={rentalDesignation} onChange={(text) => setRentalDesignation(text)} label={t('name')} required />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            <CustomTextInput value={observations} onChange={(text) => setObservations(text)} label={t('observations')} multiline />

            {activity.stepMode !== stepModes.VALIDATE && (
                <ToggleLine
                    checked={isCreateTransfer}
                    onChange={(checked) => setIsCreateTransfer(checked)}
                    title={capitalizeFirstLetter(t('createTransfer'))}
                    disabled={parseInt(selectedState.value) === materialState.PRESENT}
                />
            )}
            {activity.stepMode === stepModes.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeInfos(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyRentalModal;
